import type { JSONContent } from '@tiptap/react';
import { z } from 'zod';

import { type CalendarItem, calendarSchema, dateSchema } from './calendar';
import { validateDocument } from './document';
import { type FocusSession, focusSchema } from './focus';
import {
  type LearningCard,
  type LearningSession,
  learningCardSchema,
  learningSessionSchema,
} from './learning';
import { type Asset, assetSchema } from './media';
import { isNoteIcon, isNoteIconTone } from './note-icon';
import {
  type Goal,
  goalSchema,
  type Person,
  type Progress,
  personSchema,
  progressSchema,
  type Review,
  reviewSchema,
} from './productivity';

export const areas = ['Inbox', 'Projects', 'Areas', 'Resources', 'Archive'] as const;

export type Area = (typeof areas)[number];
export type ColumnType = 'text' | 'number' | 'date' | 'checkbox' | 'select' | 'multi' | 'url';
export type Cell = string | number | boolean | string[] | null;

export type Column = {
  id: string;
  name: string;
  type: ColumnType;
  options: (string | { id: string; label: string })[];
};

export type TableRow = { id: string; cells: Record<string, Cell> };

export type TableFilter = {
  column: string;
  operator: 'contains' | 'equals' | 'gt' | 'lt' | 'empty';
  value: string;
};

export type TypedTable = {
  filters?: TableFilter[];
  sorts?: { column: string; direction: 'asc' | 'desc' }[];
  columns: Column[];
  rows: TableRow[];
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  filterColumn: string;
  filterOperator: 'contains' | 'equals' | 'gt' | 'lt' | 'empty';
  filterValue: string;
};

export type Note = {
  locked?: boolean;
  icon?: string | null;
  iconTone?: string | null;
  trashed?: boolean;
  title: string;
  area: Area;
  parentId: string | null;
  content: JSONContent;
  updatedAt: string;
};

export type Task = {
  trashed?: boolean;
  dueDate?: string | null;
  completedOn?: string | null;
  estimateMinutes?: number | null;
  /** Legacy text retained for old records/exports; waiting behavior uses personId only. */
  waitingOn?: string;
  personId?: string | null;
  projectId?: string | null;
  areaId?: string | null;
  dropped?: boolean;
  title: string;
  noteId: string | null;
  done: boolean;
  today: boolean;
  steps: { id: string; text: string; done: boolean }[];
  stoppingPoint: string;
  stoppingDraft?: string;
  focus?: {
    runningSince: number | null;
    elapsedMs: number;
    sessionOpen: boolean;
    lastBreakMs: number;
  };
};

export type VaultItem = {
  projectId?: string | null;
  type?: 'login' | 'development' | 'recovery' | 'file' | 'card' | 'ssh';
  card?: { holder: string; number: string; expiry: string; cvv: string };
  ssh?: { publicKey: string; privateKey: string; passphrase: string; host: string };
  title: string;
  username: string;
  password: string;
  url: string;
  notes: string;
  trashed: boolean;
  pinned?: boolean;
  file?: { name: string; type: string; data: string };
};

export type WorkspaceSettingsValue = {
  timezone: string;
  dailyChecks?: Record<string, 'manageable' | 'mixed' | 'overloaded'>;
};

export type EntityValue =
  | Note
  | Task
  | VaultItem
  | CalendarItem
  | Goal
  | Progress
  | Review
  | Person
  | FocusSession
  | LearningCard
  | LearningSession
  | WorkspaceSettingsValue
  | Asset;

export type Kind =
  | 'note'
  | 'task'
  | 'vault'
  | 'calendar'
  | 'goal'
  | 'progress'
  | 'review'
  | 'person'
  | 'focus'
  | 'learning-card'
  | 'learning-session'
  | 'settings'
  | 'asset';

export type RecordData = {
  id: string;
  kind: Kind;
  value: EntityValue;
  revision: number;
  conflict: boolean;
  updatedAt: string;
  deleted?: boolean;
};

export type LocalRecord = RecordData & {
  operationId?: string;
  baseRevision: number;
  localVersion: number;
  /**
   * The server's version of a record this device edited while another device
   * saved it. The edit is held back, and neither version replaces the other,
   * until the owner chooses one (`resolveConflict` in `src/lib/engine.ts`).
   */
  competing?: RecordData;
  /**
   * Another device's open focus session, when this device opened a session
   * of its own that the server refused because only one may run at a time.
   * Held back like `competing` until the owner keeps one of the two.
   */
  clash?: RecordData;
  /**
   * True while everything waiting to upload was written by the app itself
   * — the focus heartbeat — rather than by the owner. Such an edit never asks
   * for a choice: another device's version simply wins.
   */
  automatic?: boolean;
};

/**
 * An operation the server did not apply because its base revision was stale.
 * `record` is what the server holds instead. Returned only to clients that ask
 * for conflicts to be held; older clients keep the last arrival.
 */
export type HeldOperation = { operationId: string; id: string; record: RecordData };

export type Mutation = {
  id: string;
  kind: Kind;
  value: EntityValue;
  baseRevision: number;
  operationId: string;
  deleted?: boolean;
};

const noteSchema = z.object({
  locked: z.boolean().optional(),
  icon: z
    .string()
    .max(32)
    .refine(isNoteIcon, 'Choose a page icon from the set, or one emoji')
    .nullable()
    .optional(),
  iconTone: z
    .string()
    .max(32)
    .refine(isNoteIconTone, 'Choose one of the shared icon background tones')
    .nullable()
    .optional(),
  trashed: z.boolean().optional(),
  title: z.string().max(500),
  area: z.enum(areas),
  parentId: z.string().nullable(),
  content: z.unknown().transform(validateDocument),
  updatedAt: z.string(),
});

const taskSchema = z.object({
  trashed: z.boolean().optional(),
  dueDate: dateSchema.nullable().optional(),
  completedOn: dateSchema.nullable().optional(),
  estimateMinutes: z.number().int().positive().max(10080).nullable().optional(),
  waitingOn: z.string().max(500).optional(),
  personId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  areaId: z.string().uuid().nullable().optional(),
  dropped: z.boolean().optional(),
  title: z.string().min(1).max(500),
  noteId: z.string().nullable(),
  done: z.boolean(),
  today: z.boolean(),
  stoppingPoint: z.string().max(5000),
  stoppingDraft: z.string().max(5000).optional(),
  steps: z
    .array(z.object({ id: z.string(), text: z.string().max(1000), done: z.boolean() }))
    .max(500),
  focus: z
    .object({
      runningSince: z.number().nullable(),
      elapsedMs: z.number().nonnegative(),
      sessionOpen: z.boolean(),
      lastBreakMs: z.number().nonnegative(),
    })
    .optional(),
});

const vaultSchema = z.object({
  card: z
    .object({
      holder: z.string().max(500),
      number: z.string().max(100),
      expiry: z.string().max(30),
      cvv: z.string().max(20),
    })
    .optional(),
  ssh: z
    .object({
      publicKey: z.string().max(50000),
      privateKey: z.string().max(100000),
      passphrase: z.string().max(10000),
      host: z.string().max(2000),
    })
    .optional(),
  projectId: z.string().uuid().nullable().optional(),
  type: z.enum(['login', 'development', 'recovery', 'file', 'card', 'ssh']).optional(),
  title: z.string().min(1).max(500),
  username: z.string().max(2000),
  password: z.string().max(10000),
  url: z
    .string()
    .max(2000)
    .refine(value => {
      if (!value) {
        return true;
      }

      try {
        return ['http:', 'https:'].includes(new URL(value).protocol);
      } catch {
        return false;
      }
    }),
  notes: z.string().max(50000),
  trashed: z.boolean(),
  pinned: z.boolean().optional(),
  file: z
    .object({
      name: z.string().max(500),
      type: z.string().max(200),
      data: z
        .string()
        .max(8_000_000)
        .regex(
          /^data:(?:[a-zA-Z0-9!#$&^_.+-]+\/[a-zA-Z0-9!#$&^_.+-]+)?;base64,[A-Za-z0-9+/]*={0,2}$/,
        ),
    })
    .optional(),
});

export const mutationSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum([
    'note',
    'task',
    'vault',
    'calendar',
    'goal',
    'progress',
    'review',
    'person',
    'focus',
    'learning-card',
    'learning-session',
    'settings',
    'asset',
  ]),
  value: z.unknown(),
  baseRevision: z.number().int().nonnegative(),
  operationId: z.string().uuid(),
  deleted: z.boolean().optional(),
});

export function validateValue(kind: Kind, value: unknown): EntityValue {
  if (kind === 'learning-card') {
    return learningCardSchema.parse(value);
  }

  if (kind === 'learning-session') {
    return learningSessionSchema.parse(value);
  }

  return (
    kind === 'asset'
      ? assetSchema.parse(value)
      : kind === 'settings'
        ? z
            .object({
              dailyChecks: z
                .record(dateSchema, z.enum(['manageable', 'mixed', 'overloaded']))
                .optional(),
              timezone: z.string().refine(zone => {
                try {
                  new Intl.DateTimeFormat('en', { timeZone: zone });

                  return true;
                } catch {
                  return false;
                }
              }),
            })
            .parse(value)
        : kind === 'focus'
          ? focusSchema.parse(value)
          : kind === 'goal'
            ? goalSchema.parse(value)
            : kind === 'progress'
              ? progressSchema.parse(value)
              : kind === 'review'
                ? reviewSchema.parse(value)
                : kind === 'person'
                  ? personSchema.parse(value)
                  : kind === 'calendar'
                    ? calendarSchema.parse(value)
                    : kind === 'note'
                      ? noteSchema.parse(value)
                      : kind === 'task'
                        ? taskSchema.parse(value)
                        : vaultSchema.parse(value)
  ) as EntityValue;
}

export function newTable(): TypedTable {
  const name = crypto.randomUUID(),
    status = crypto.randomUUID(),
    count = crypto.randomUUID();

  return {
    columns: [
      { id: name, name: 'Item', type: 'text', options: [] },
      {
        id: status,
        name: 'Status',
        type: 'select',
        options: ['To do', 'In progress', 'Done'].map(label => ({ id: label, label })),
      },
      { id: count, name: 'Estimate', type: 'number', options: [] },
    ],
    rows: [
      { id: crypto.randomUUID(), cells: { [name]: 'First item', [status]: 'To do', [count]: 15 } },
    ],
    sortColumn: '',
    sortDirection: 'asc',
    filterColumn: '',
    filterOperator: 'contains',
    filterValue: '',
  };
}
